import type { Updaters } from '~/adapter/indexedDB/types'
import type { OfflineDownloadInfoSchemaV4 } from '../../4/downloadInfo/downloadInfo'
import type { EmployeeSchemaV4 } from '../../4/employee/employee'
import type { FeatureFlagSchemaV4 } from '../../4/featureFlag/featureFlag'
import type {
  MultipleChoiceSetSchemaV4,
  TemplateMultipleChoiceSetRelationSchemaV4,
} from '../../4/multipleChoiceSet/multipleChoiceSet'
import type { NodeV4 } from '../../4/report/node/node'
import type { QuestionNodeV4 as QuestionNodeV4FromV4 } from '../../4/report/node/questionNode/questionNode'
import type {
  ReportManualSchemaV4,
  ReportNodeSchemaV4,
  ReportSchemaV4,
} from '../../4/report/report'
import type { ScheduleItemSchemaV4 } from '../../4/schedule/scheduleItem'
import type {
  TemplateNodeSchemaV4,
  TemplateSchemaV4,
} from '../../4/template/template'
import type { TemplateMediaSchemaV4 } from '../../4/template/templateMedia'
import type { VersionSchemaV4 } from '../../4/version/version'
import type { OfflineDownloadInfoSchemaV5 } from '../downloadInfo/downloadInfo'
import type { EmployeeSchemaV5 } from '../employee/employee'
import type { FeatureFlagSchemaV5 } from '../featureFlag/featureFlag'
import type {
  MultipleChoiceSetSchemaV5,
  TemplateMultipleChoiceSetRelationSchemaV5,
} from '../multipleChoiceSet/multipleChoiceSet'
import type { NodeV5 } from '../report/node/node'
import type { QuestionNodeV5 } from '../report/node/questionNode/questionNode'
import type {
  ReportManualSchemaV5,
  ReportNodeSchemaV5,
  ReportSchemaV5,
} from '../report/report'
import type { ScheduleItemSchemaV5 } from '../schedule/scheduleItem'
import type {
  TemplateNodeSchemaV5,
  TemplateSchemaV5,
} from '../template/template'
import type { TemplateMediaSchemaV5 } from '../template/templateMedia'
import type { VersionSchemaV5 } from '../version/version'

const version = 5
type versionType = typeof version

export const migration: Pick<
  Updaters[versionType],
  'upgradeBefore' | 'migrations'
> = {
  upgradeBefore: async () => {},
  migrations: {
    versions: (prev: VersionSchemaV4): VersionSchemaV5 => {
      return {
        ...prev,
        // NOTICE: version テーブルのインクリメントを忘れないこと
        version,
      }
    },
    featureFlags: (prev: FeatureFlagSchemaV4): FeatureFlagSchemaV5 => {
      return prev
    },
    reports: (prev: ReportSchemaV4): ReportSchemaV5 => {
      // ReportMode の product フィールドは既に V4 に存在するため、そのまま返す
      return prev
    },
    reportNodes: (prev: ReportNodeSchemaV4): ReportNodeSchemaV5 => {
      const current = {
        ...prev,
        data: {
          ...prev.data,
          nodes: migrateNodes(prev.data.nodes),
        },
      }
      return current
    },
    reportManuals: (prev: ReportManualSchemaV4): ReportManualSchemaV5 => {
      return prev
    },
    employees: (prev: EmployeeSchemaV4): EmployeeSchemaV5 => {
      return prev
    },
    offlineDownloadInfos: (
      prev: OfflineDownloadInfoSchemaV4,
    ): OfflineDownloadInfoSchemaV5 => {
      return prev
    },
    scheduleItems: (prev: ScheduleItemSchemaV4): ScheduleItemSchemaV5 => {
      return prev
    },
    templates: (prev: TemplateSchemaV4): TemplateSchemaV5 => {
      return prev
    },
    templateNodes: (prev: TemplateNodeSchemaV4): TemplateNodeSchemaV5 => {
      const current = {
        ...prev,
        data: {
          ...prev.data,
          nodes: migrateNodes(prev.data.nodes),
        },
      }
      return current
    },
    templateMedia: (prev: TemplateMediaSchemaV4): TemplateMediaSchemaV5 => {
      return prev
    },
    multipleChoiceSets: (
      prev: MultipleChoiceSetSchemaV4,
    ): MultipleChoiceSetSchemaV5 => {
      return prev
    },
    templateMultipleChoiceSetRelations: (
      prev: TemplateMultipleChoiceSetRelationSchemaV4,
    ): TemplateMultipleChoiceSetRelationSchemaV5 => {
      return prev
    },
  },
}

const migrateNodes = (prevNodes: ReportNodeSchemaV4['data']['nodes']) => {
  const nodes: ReportNodeSchemaV5['data']['nodes'] = {}
  for (const node of Object.values(prevNodes)) {
    if (node) {
      nodes[node.id] = migrateNode(node)
    }
  }
  return nodes
}

const migrateNode = (prevNode: NodeV4): NodeV5 => {
  switch (prevNode.type) {
    case 'logic': {
      return prevNode
    }
    case 'question': {
      return migrateQuestionNode(prevNode)
    }
    case 'section': {
      return prevNode
    }
    case 'page': {
      return prevNode
    }
    default:
      return prevNode satisfies never
  }
}

type QuestionNodeSchemaByType<
  T extends QuestionNodeV4FromV4['questionType'],
  K extends QuestionNodeV4FromV4 = QuestionNodeV4FromV4,
> = K extends {
  questionType: T
}
  ? K
  : never

const migrateQuestionNode = <T extends QuestionNodeV4FromV4['questionType']>(
  prevNode: QuestionNodeSchemaByType<T>,
): QuestionNodeV5 => {
  // 数値質問の場合、defaultAnswer と isRuleFromProductMaster を追加
  if (prevNode.questionType === 'number') {
    return {
      ...prevNode,
      question: {
        ...prevNode.question,
        // 既存データには情報がないため、デフォルト値を設定
        defaultAnswer: undefined,
        isRuleFromProductMaster: false,
      },
    } as QuestionNodeV5
  }

  // テキスト質問の場合、defaultAnswer を追加
  if (prevNode.questionType === 'text') {
    return {
      ...prevNode,
      question: {
        ...prevNode.question,
        // 既存データには情報がないため、デフォルト値を設定
        defaultAnswer: undefined,
      },
    } as QuestionNodeV5
  }

  // その他の質問タイプはそのまま返す
  return prevNode as unknown as QuestionNodeV5
}
